import { symmetricKey } from './symmetricKey';

/**
 * No-reference overlay helpers for the Clonality clusters table.
 *
 * Default closely-related text (bold clusters, italic singleton neighbors) comes from
 * clusterSamples and SNP distances. This module adds an orange overlay only for pairs
 * with skip_reason NO_REFERENCE_ABOVE_IDENTITY_THRESHOLD.
 */
export const SKIP_REASON_NO_REFERENCE = 'NO_REFERENCE_ABOVE_IDENTITY_THRESHOLD';
export const NO_REFERENCE_LABEL = 'Potential - no suitable reference';

export const isNoReferenceSkipReason = (skipReason) => (
  skipReason === SKIP_REASON_NO_REFERENCE
);

export const noReferenceLabelForSkipReason = (skipReason) => (
  isNoReferenceSkipReason(skipReason) ? NO_REFERENCE_LABEL : skipReason
);

const isNoReferencePair = (comparisons, leftId, rightId) => {
  const entry = comparisons.get(symmetricKey(leftId, rightId));
  return entry != null && isNoReferenceSkipReason(entry.skipReason);
};

const isInClonalCluster = (sampleId, clusterIdBySampleId, clusterById) => {
  const clusterId = clusterIdBySampleId.get(sampleId);
  if (clusterId == null) {
    return false;
  }
  const cluster = clusterById.get(clusterId);
  return cluster?.kind === 'clonal' && cluster.size >= 2;
};

const getClonalClusterId = (sampleId, clusterIdBySampleId, clusterById) => (
  isInClonalCluster(sampleId, clusterIdBySampleId, clusterById)
    ? clusterIdBySampleId.get(sampleId)
    : null
);

export const shouldDisplayNoRefLink = ({
  fromSampleId,
  toSampleId,
  clusterIdBySampleId,
  clusterById
}) => {
  const fromClusterId = getClonalClusterId(fromSampleId, clusterIdBySampleId, clusterById);
  const toClusterId = getClonalClusterId(toSampleId, clusterIdBySampleId, clusterById);

  if (fromClusterId != null && toClusterId === fromClusterId) {
    return false;
  }
  if (fromClusterId != null && toClusterId == null) {
    return false;
  }
  return true;
};

const formatSingletonGroup = (sampleNames) => {
  const sorted = [...sampleNames].sort((left, right) => left.localeCompare(right));
  if (sorted.length === 0) {
    return null;
  }
  if (sorted.length === 1) {
    return sorted[0];
  }
  const additionalCount = sorted.length - 1;
  const label = additionalCount === 1 ? 'sample' : 'samples';
  return `${sorted[0]} + ${additionalCount} closely related ${label}`;
};

export const sampleIdsWithAnyNoReferencePair = (samples, comparisons) => {
  const ids = new Set();
  samples.forEach((sample) => {
    const hasNoRef = samples.some((other) => (
      other.id !== sample.id
      && isNoReferencePair(comparisons, sample.id, other.id)
    ));
    if (hasNoRef) {
      ids.add(sample.id);
    }
  });
  return ids;
};

const defaultClusterLabelsForSample = (sampleId, closelyRelatedBySampleId) => (
  new Set(
    (closelyRelatedBySampleId.get(sampleId) || [])
      .filter((entry) => entry.kind === 'clonal' && entry.label)
      .map((entry) => entry.label)
  )
);

const defaultSingletonNamesForSample = (sampleId, closelySingletonsBySampleId) => (
  new Set(
    (closelySingletonsBySampleId.get(sampleId) || [])
      .map((entry) => entry.sampleName)
      .filter(Boolean)
  )
);

export const deriveNoReferenceTableLinksBySampleId = ({
  samples,
  comparisons,
  clusters,
  clusterIdBySampleId,
  closelyRelatedBySampleId = new Map(),
  closelySingletonsBySampleId = new Map()
}) => {
  const clusterById = new Map(clusters.map((cluster) => [cluster.id, cluster]));
  const sampleById = new Map(samples.map((sample) => [sample.id, sample]));
  const linksBySampleId = new Map();

  samples.forEach((sample) => {
    const defaultClusterLabels = defaultClusterLabelsForSample(
      sample.id,
      closelyRelatedBySampleId
    );
    const defaultSingletonNames = defaultSingletonNamesForSample(
      sample.id,
      closelySingletonsBySampleId
    );

    const displayableTargets = samples.filter((target) => {
      if (
        target.id === sample.id
        || !isNoReferencePair(comparisons, sample.id, target.id)
        || !shouldDisplayNoRefLink({
          fromSampleId: sample.id,
          toSampleId: target.id,
          clusterIdBySampleId,
          clusterById
        })
      ) {
        return false;
      }

      const targetClusterId = getClonalClusterId(target.id, clusterIdBySampleId, clusterById);
      if (targetClusterId != null) {
        const targetLabel = clusterById.get(targetClusterId)?.label;
        if (targetLabel && defaultClusterLabels.has(targetLabel)) {
          return false;
        }
      }

      const targetName = sampleById.get(target.id)?.name;
      if (targetName && defaultSingletonNames.has(targetName)) {
        return false;
      }

      return true;
    });

    const clusterGroups = new Map();
    const singletonNames = [];

    displayableTargets.forEach((target) => {
      const targetClusterId = getClonalClusterId(target.id, clusterIdBySampleId, clusterById);
      if (targetClusterId != null) {
        const cluster = clusterById.get(targetClusterId);
        if (!clusterGroups.has(targetClusterId)) {
          clusterGroups.set(targetClusterId, {
            label: cluster?.label || '',
            sampleNames: []
          });
        }
        const name = sampleById.get(target.id)?.name || String(target.id);
        clusterGroups.get(targetClusterId).sampleNames.push(name);
        return;
      }

      const name = sampleById.get(target.id)?.name || String(target.id);
      singletonNames.push(name);
    });

    const clusterSegments = [...clusterGroups.values()]
      .sort((left, right) => String(left.label).localeCompare(String(right.label)))
      .map((group) => (group.label ? `Cluster ${group.label}` : ''));

    const singletonSegment = formatSingletonGroup(singletonNames);
    const segments = [];

    if (clusterSegments.length > 0 && singletonSegment) {
      segments.push(`${clusterSegments.join(', ')}, ${singletonSegment}`);
    } else if (clusterSegments.length > 0) {
      segments.push(...clusterSegments);
    } else if (singletonSegment) {
      segments.push(singletonSegment);
    }

    linksBySampleId.set(sample.id, {
      segments,
      showChip: segments.length > 0
    });
  });

  return linksBySampleId;
};

/** @deprecated use sampleIdsWithAnyNoReferencePair */
export const deriveNoReferenceSampleIds = sampleIdsWithAnyNoReferencePair;
